import { memo } from 'react';
import { cn } from '@/lib/utils';
import { priorityKind } from '@/lib/cobalt-helpers';

interface Props {
  /** Picklist label, e.g. "High", "Critical", "Medium". */
  priority: string | null | undefined;
  /** Compact = dot + label, no background pill. */
  compact?: boolean;
  className?: string;
}

/**
 * Priority dot pill. Uses CSS variables `--pri-{critical|high|medium|low}` so
 * the same component picks up new priority schemes from the token system.
 *
 * Compact mode (dot + text, no background) is for table cells where a full
 * badge would be too heavy. Full mode is for cards and detail pages.
 */
const COLOR_VAR = {
  critical: 'hsl(var(--pri-critical))',
  high:     'hsl(var(--pri-high))',
  medium:   'hsl(var(--pri-medium))',
  low:      'hsl(var(--pri-low))',
};
const TINT_VAR = {
  critical: 'hsl(var(--pri-critical) / 0.12)',
  high:     'hsl(var(--pri-high) / 0.12)',
  medium:   'hsl(var(--pri-medium) / 0.12)',
  low:      'hsl(var(--pri-low) / 0.12)',
};

// Memoised: it sits in every row of the ticket grids.
export const PriorityTag = memo(function PriorityTag({ priority, compact, className }: Props) {
  if (!priority) return null;
  const k = priorityKind(priority);
  const colorVar = COLOR_VAR[k];
  const tintVar = TINT_VAR[k];

  if (compact) {
    return (
      <span className={cn('inline-flex items-center gap-1.5 text-[11.5px] font-medium', className)}
            style={{ color: 'hsl(var(--foreground) / 0.78)' }}>
        <span className="h-[7px] w-[7px] rounded-full" style={{ background: colorVar }} />
        {priority}
      </span>
    );
  }

  return (
    <span
      className={cn('inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11.5px] font-medium', className)}
      style={{ color: colorVar, background: tintVar }}
    >
      <span className="h-[7px] w-[7px] rounded-full" style={{ background: colorVar }} />
      {priority}
    </span>
  );
});
